import { Typography, Button, List, Stack, IconButton } from "@mui/material"
import { useContextMenu, ContextMenu } from "components/context-menu"
import { useDialogStore } from "dialogs"
import { action } from "mobx"
import { observer } from "mobx-react-lite"
import { nanoid } from "nanoid"
import { useMainStore } from "store"
import Scheme from "store/schema"
import { increment, num, numeral } from "utils/lang"
import { useLocation } from "wouter"
import { Column, Header, SchemaListItem } from "./styled"
import { scrollbarStyle } from "styles/global-styles"

import CopyIcon from "components/icons/editor/copy"
import DeleteIcon from "components/icons/editor/delete"
import DashboardAddIcon from "components/icons/other/dashboard-add"
import SchemaIcon from "components/icons/other/schema"
import ImportIcon from "components/icons/other/import"
import ExportIcon from "components/icons/other/export"
import SettingsIcon from "components/icons/other/settings"

type Props = {
  onImportClick: (project: any) => void
}

const SchemesList = observer(({ onImportClick }: Props) => {

  const dialog = useDialogStore()
  const [ , setLocation ] = useLocation()
  const store = useMainStore()

  const createScheme = () => {
    setLocation("/schemes/"+nanoid(4))
  }
  const contextMenuSchemes = useContextMenu(() => ([
    { title: "Экспорт схем", icon: <ExportIcon/>, onClick: () => dialog.open("exportSchemes") },
    { title: "Импорт схем", icon: <ImportIcon/>, onClick: () => onImportClick(null) },
  ]))


  const deleteScheme = (scheme: Scheme) => {
    const contents = store.getContentsWithScheme(scheme.id)
    const content = (
      <>
        Схема «{scheme.data.name}» будет удалена
        { contents.length > 0 && (
          <span style={{ display: "block", marginTop: "0.2em" }}>
            <Typography color="primary.main" mr={0.5} component="span">Внимание!</Typography>
            С этой схемой {numeral(contents.length, "связан", "связаны")} { num(contents.length, "блок", "блока", "блоков") }
          </span>
        ) }
      </>
    )
    dialog.open("delete", { content, onConfirm: () => {
      store.deleteSchema(scheme.id)
    } })
  }

  const dublicate = action((scheme: Scheme) => {
    const newScheme = new Scheme()
    newScheme.data = JSON.parse(JSON.stringify(scheme.data))
    const names = Array.from(store.schemes.values()).map(item => item.data.name)
    newScheme.data.name = increment(scheme.data.name, names)
    
    const entries = Array.from(store.schemes.entries())
    const index = entries.findIndex(item => item[0] === scheme.id)
    entries.splice(index+1, 0, [ newScheme.id, newScheme ])
    store.schemes.replace(entries)

    store.saveSchema(newScheme)
  })

  return (
    <Column 
      onContextMenu={contextMenuSchemes.open}
      sx={{ maxWidth: 600, bgcolor: "background.paper", ml: "auto", my: 5, borderRadius: "20px 0 0 20px" }}
    >
      <Header sx={{ px: 4, py: 3 }}>
        <Typography fontSize={24} fontWeight={700}>Схемы данных</Typography>
        <Button variant="contained" sx={{ ml: "auto" }} onClick={createScheme}>
          <DashboardAddIcon sx={{ mr: 1, fontSize: 16 }}/>
          Создать схему
        </Button>
      </Header>
      <List disablePadding sx={{ ...scrollbarStyle }}>
        { Array.from(store.schemes.values()).map(item => (
          <SchemaListItem 
            key={item.id}
            sx={{ px: 4 }} 
            onClick={e => e.target === e.currentTarget && setLocation(`/schemes/${item.id}`) }
          >
            <SchemaIcon sx={{ mr: 2.5, color: "text.secondary", pointerEvents: "none" }}/>
            <Stack sx={{ mr: "auto", pointerEvents: "none" }}>
              <Typography fontWeight={700}>{ item.data.name || "Без названия" }</Typography>
              <Typography color="text.secondary">
                { num(Object.keys(item.data.blocks).length, "блок", "блока", "блоков") }
              </Typography>
            </Stack>
            <IconButton 
              onClick={() => dialog.open("schemaSettings", { schemaId: item.id })}
              sx={{ ml: 1.5, color: "primary.main", bgcolor: "background.default" }} 
              className="show-on-hover"
            >
              <SettingsIcon/>
            </IconButton>
            <IconButton 
              onClick={() => dublicate(item)}
              sx={{ ml: 1.5, color: "primary.main", bgcolor: "background.default" }} 
              className="show-on-hover"
            >
              <CopyIcon/>
            </IconButton>
            <IconButton 
              onClick={() => deleteScheme(item)}
              sx={{ ml: 1.5, color: "primary.main", bgcolor: "background.default" }} 
              className="show-on-hover"
            >
              <DeleteIcon/>
            </IconButton>
          </SchemaListItem>
        ))}
      </List>
      <ContextMenu store={contextMenuSchemes}/>
    </Column>
  )
})

export default SchemesList